<template>
  <div class="premium-checkout fade-in">
    <div class="container">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <button class="btn btn-outline-light btn-sm" type="button" @click="$emit('back')">
          <i class="bi bi-arrow-left"></i>
          Voltar
        </button>
        <span class="badge bg-danger-subtle text-dark" v-if="deliveryWarning">
          <i class="bi bi-exclamation-triangle me-1"></i>
          {{ deliveryWarning }}
        </span>
      </div>

      <div class="row g-4">
        <div class="col-lg-7">
          <div class="card checkout-card">
            <div class="card-body p-4 p-lg-5">
              <h2 class="checkout-title">Gerar link seguro de pagamento</h2>
              <p class="text-muted mb-4">
                Vamos criar um link de pagamento na InfinitePay e redirecionar você para finalizar a compra. Os dados ficam
                registrados para ativarmos seu plano premium assim que o pagamento for confirmado.
              </p>

              <form class="checkout-form" @submit.prevent="submitForm">
                <div class="row g-3">
                  <div class="col-12">
                    <label class="form-label">Nome completo</label>
                    <input
                      v-model.trim="form.fullName"
                      type="text"
                      class="form-control"
                      placeholder="Seu nome completo"
                      required
                    >
                  </div>
                  <div class="col-12">
                    <label class="form-label">Telefone para contato (opcional)</label>
                    <input
                      v-model="form.phoneNumber"
                      type="tel"
                      class="form-control"
                      inputmode="tel"
                      placeholder="(11) 99999-9999"
                    >
                  </div>
                  <div class="col-12">
                    <label class="form-label">Endereço para entrega (opcional)</label>
                    <div class="row g-3">
                      <div class="col-sm-4">
                        <input
                          v-model="form.address.cep"
                          type="text"
                          class="form-control"
                          inputmode="numeric"
                          maxlength="9"
                          placeholder="CEP"
                        >
                      </div>
                      <div class="col-sm-4">
                        <input
                          v-model.trim="form.address.number"
                          type="text"
                          class="form-control"
                          inputmode="numeric"
                          maxlength="10"
                          placeholder="Número"
                        >
                      </div>
                      <div class="col-sm-4">
                        <input
                          v-model.trim="form.address.complement"
                          type="text"
                          class="form-control"
                          maxlength="60"
                          placeholder="Complemento"
                        >
                      </div>
                    </div>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Observações</label>
                    <textarea
                      v-model.trim="form.notes"
                      class="form-control"
                      rows="3"
                      placeholder="Informações adicionais para a curadoria"
                    ></textarea>
                  </div>
                </div>

                <div v-if="errorMessage" class="alert alert-danger mt-4" role="alert">
                  {{ errorMessage }}
                </div>
                <div v-if="successMessage" class="alert alert-success mt-4" role="alert">
                  {{ successMessage }}
                </div>

                <button class="btn btn-vampire btn-lg w-100 mt-4" type="submit" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {{ loading ? 'Gerando link seguro...' : 'Continuar com InfinitePay' }}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div class="col-lg-5">
          <aside class="card checkout-summary">
            <div class="card-body p-4">
              <h3 class="summary-title">Resumo do plano</h3>
              <p class="text-muted">
                Confirme os dados ao lado para gerar o link seguro e concluir o pagamento pela InfinitePay. Após a confirmação,
                liberamos o plano premium na sua conta.
              </p>

              <ul class="list-unstyled summary-list">
                <li>
                  <i class="bi bi-gem"></i>
                  Acesso premium — R$ 19,90/mês
                </li>
                <li>
                  <i class="bi bi-files"></i>
                  Até 5 fichas de personagem simultâneas
                </li>
                <li>
                  <i class="bi bi-lock"></i>
                  Salas privadas ilimitadas no chat
                </li>
                <li>
                  <i class="bi bi-ticket"></i>
                  Descontos exclusivos em parceiros
                </li>
              </ul>

              <div class="alert alert-warning mt-4" role="alert">
                <i class="bi bi-shield-lock me-2"></i>
                Pagamentos são conferidos pela curadoria depois do retorno da InfinitePay. Você recebe as instruções e o
                comprovante no e-mail cadastrado (<strong>{{ userEmail }}</strong>).
              </div>

              <div class="admin-hint mt-4">
                <h4>Painel interno</h4>
                <p>
                  Os pedidos ficam registrados para que você possa controlá-los em um painel privado (intranet) e ativar o plano
                  premium quando o pagamento for confirmado.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'

export default {
  name: 'PremiumCheckoutView',
  props: {
    user: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        fullName: '',
        phoneNumber: '',
        notes: '',
        address: {
          cep: '',
          number: '',
          complement: ''
        }
      },
      loading: false,
      successMessage: '',
      errorMessage: '',
      deliveryWarning: ''
    }
  },
  computed: {
    userEmail() {
      return this.user?.email || 'seu-email'
    }
  },
  watch: {
    user: {
      handler(newVal) {
        if (newVal?.name && !this.form.fullName) {
          this.form.fullName = newVal.name
        }
      },
      immediate: true
    }
  },
  methods: {
    sanitizePayload() {
      const phoneDigits = this.form.phoneNumber.replace(/[^\d+]/g, '')
      const cepDigits = this.form.address.cep.replace(/\D/g, '')

      return {
        fullName: this.form.fullName.trim(),
        phoneNumber: phoneDigits,
        notes: this.form.notes.trim(),
        address: {
          cep: cepDigits,
          number: this.form.address.number.trim(),
          complement: this.form.address.complement.trim()
        }
      }
    },
    validatePayload(payload) {
      if (!payload.fullName) {
        return 'Informe seu nome completo para gerar o link de pagamento.'
      }

      if (payload.phoneNumber && payload.phoneNumber.replace(/\D/g, '').length < 10) {
        return 'Informe um telefone válido com DDD ou deixe o campo em branco.'
      }

      if (payload.address.cep && payload.address.cep.replace(/\D/g, '').length !== 8) {
        return 'Informe um CEP válido com 8 dígitos ou deixe o campo em branco.'
      }

      if (payload.address.cep && !payload.address.number) {
        return 'Informe o número do endereço para entrega ou limpe o CEP.'
      }

      return ''
    },
    resetForm() {
      this.form.phoneNumber = ''
      this.form.notes = ''
      this.form.address = { cep: '', number: '', complement: '' }
    },
    async submitForm() {
      if (this.loading) {
        return
      }

      this.errorMessage = ''
      this.successMessage = ''
      this.deliveryWarning = ''

      const payload = this.sanitizePayload()
      const validationError = this.validatePayload(payload)
      if (validationError) {
        this.errorMessage = validationError
        return
      }

      this.loading = true
      try {
        const { data } = await api.post('/membership/checkout', payload)
        this.successMessage = data?.message || 'Link gerado! Você será redirecionado para pagar com a InfinitePay.'
        this.resetForm()
        this.$emit('submitted', data)

        if (data?.checkoutUrl) {
          window.location.href = data.checkoutUrl
        } else {
          this.errorMessage = 'Não foi possível abrir o link de pagamento. Tente novamente.'
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.error || 'Não foi possível concluir a assinatura. Tente novamente mais tarde.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.premium-checkout {
  padding: 2rem 0 4rem;
}

.checkout-card {
  background: rgba(17, 17, 17, 0.92);
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-radius: 24px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.45);
}

.checkout-title {
  font-size: 2rem;
  font-weight: 600;
  color: var(--vampire-gold);
}

.checkout-form label {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.checkout-form .form-control {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(212, 175, 55, 0.2);
  color: #f8f9fa;
}

.checkout-form .form-control:focus {
  background: rgba(255, 255, 255, 0.08);
  border-color: var(--vampire-gold);
  box-shadow: 0 0 0 0.2rem rgba(212, 175, 55, 0.15);
}

.checkout-summary {
  background: rgba(26, 13, 13, 0.9);
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-radius: 24px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
}

.summary-title {
  color: var(--vampire-gold);
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.summary-list li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: rgba(255, 255, 255, 0.85);
}

.summary-list i {
  color: var(--vampire-gold);
}

.admin-hint {
  background: rgba(17, 17, 17, 0.85);
  border: 1px dashed rgba(212, 175, 55, 0.4);
  border-radius: 16px;
  padding: 1.5rem;
}

.admin-hint h4 {
  color: var(--vampire-gold);
  margin-bottom: 0.5rem;
}

.badge.bg-danger-subtle {
  background: rgba(255, 201, 201, 0.9) !important;
}

@media (max-width: 991.98px) {
  .premium-checkout {
    padding-top: 1rem;
  }
}
</style>
